// src/pages/Partners/UploadPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './UploadPage.scss';

const api = axios.create({
    baseURL: 'https://api.display-promo.ru',
});

const UploadPage = () => {
    const { hash } = useParams();

    const [items, setItems] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);
    const [idCounter, setIdCounter] = useState(1);

    const [companyName, setCompanyName] = useState('Рекламная компания');
    const [address, setAddress] = useState('Адрес точки');
    const [partnerCode, setPartnerCode] = useState(null);

    const [adTitle, setAdTitle] = useState('');   // Название ролика
    const [adUrl, setAdUrl] = useState(null);    // URL ролика
    const [adType, setAdType] = useState(null);  // MIME-тип (video/mp4, image/png, ...)
    const [adId, setAdId] = useState(null);      // ID ролика для команды воспроизведения

    const [loadingInfo, setLoadingInfo] = useState(true);
    const [infoError, setInfoError] = useState(null);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const [commandSending, setCommandSending] = useState(false);
    const [commandMessage, setCommandMessage] = useState(null);

    // Загрузка данных по hash + уже загруженные отчёты
    useEffect(() => {
        if (!hash) return;

        setLoadingInfo(true);
        setInfoError(null);

        api.get(`/api/partners/${hash}`)
            .then((res) => {
                const data = res.data || {};
                const company = data.company || {};
                const reports = data.reports || [];
                const ad = data.ad || {};

                setCompanyName(company.name || 'Рекламная компания');
                setAddress(company.address || 'Адрес точки');
                setPartnerCode(data.partnerCode ?? null);

                setAdTitle(ad.title || '');
                setAdUrl(ad.url || null);
                setAdType(ad.type || null);
                setAdId(ad.id ?? null);

                const loadedItems = reports.map((r, index) => ({
                    id: r.id != null ? `server-${r.id}` : `server-${index}`,
                    url: r.url,
                    file: null,
                    type: r.type === 'video' ? 'video' : 'image',
                    uploaded: true,
                    serverUrl: r.url,
                    name: r.type === 'video' ? 'Видео отчёт' : 'Фото отчёт',
                    size: r.size ?? null,
                }));

                setItems(loadedItems);
            })
            .catch((err) => {
                console.error(err);
                setInfoError('Не удалось загрузить данные по точке.');
            })
            .finally(() => {
                setLoadingInfo(false);
            });
    }, [hash]);

    // Отправка новых файлов
    const uploadFilesToServer = async (newItems) => {
        if (!hash || !newItems.length) return;

        setUploading(true);
        setUploadError(null);

        const formData = new FormData();
        newItems.forEach((item) => {
            formData.append('files[]', item.file);
        });

        try {
            const res = await api.post(`/api/partners/${hash}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const uploaded = res.data?.files || [];

            const updatedNewItems = newItems.map((item, index) => {
                const srv = uploaded[index] || {};
                return {
                    ...item,
                    uploaded: true,
                    serverUrl: srv.url || item.url,
                    url: srv.url || item.url,
                    size: srv.size ?? item.size,
                };
            });

            setItems((prev) => [...updatedNewItems, ...prev]);
        } catch (e) {
            console.error(e);
            setUploadError('Ошибка при загрузке файлов. Попробуйте ещё раз.');
            setItems((prev) => [...newItems, ...prev]);
        } finally {
            setUploading(false);
        }
    };

    const handleFiles = (files) => {
        if (!files) return;

        const newItems = [];
        let nextId = idCounter;

        Array.from(files).forEach((file) => {
            if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                return;
            }

            const type = file.type.startsWith('image/') ? 'image' : 'video';
            const url = URL.createObjectURL(file);

            newItems.push({
                id: `local-${nextId++}`,
                url,
                file,
                type,
                uploaded: false,
                serverUrl: null,
                name: file.name,
                size: file.size,
            });
        });

        if (!newItems.length) return;

        setIdCounter(nextId);
        uploadFilesToServer(newItems);
    };

    const onInputChange = (e) => {
        handleFiles(e.target.files);
        e.target.value = '';
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const openPreview = (item) => {
        setPreviewItem(item);
    };

    const closePreview = () => {
        setPreviewItem(null);
    };

    const renderAdMedia = () => {
        if (!adUrl) return null;

        const isImage = adType && adType.startsWith('image/');
        const isVideo = adType && adType.startsWith('video/');

        return (
            <div className="partners-upload-page__ad-media">
                {isImage && (
                    <img
                        src={adUrl}
                        alt={adTitle || 'Рекламный материал'}
                        className="partners-upload-page__ad-image"
                    />
                )}
                {!isImage && (
                    <video
                        src={adUrl}
                        controls
                        muted
                        className="partners-upload-page__ad-video"
                    />
                )}
            </div>
        );
    };

    const handlePlayCommand = async () => {
        if (!partnerCode || !adId) {
            setCommandMessage('Невозможно отправить команду: не найден код точки или ID ролика.');
            return;
        }

        setCommandSending(true);
        setCommandMessage(null);

        try {
            await api.post(`/api/device/${partnerCode}/command/${adId}`);
            setCommandMessage('Команда отправлена. Ролик начнёт воспроизводиться в течение 5 секунд.');
        } catch (e) {
            console.error(e);
            setCommandMessage('Не удалось отправить команду на устройство.');
        } finally {
            setCommandSending(false);
        }
    };

    return (
        <div className="partners-upload-page">
            <header className="partners-upload-page__header">
                <div className="partners-upload-page__company">
                    <h1 className="partners-upload-page__company-name">
                        {loadingInfo
                            ? 'Загрузка...'
                            : adTitle
                                ? `Реклама — ${adTitle}`
                                : 'Реклама'}
                    </h1>

                    <div className="partners-upload-page__company-block">
                        <div className="partners-upload-page__company-row">
                            <span className="partners-upload-page__company-label">Адрес точки</span>
                            <div className="partners-upload-page__company-value">
                                {loadingInfo ? 'Получаем адрес...' : address}
                            </div>
                        </div>

                        <div className="partners-upload-page__company-row">
                            <span className="partners-upload-page__company-label">Партнёрский код</span>
                            <div className="partners-upload-page__company-value">
                                <strong>{partnerCode !== null ? partnerCode : '—'}</strong>
                            </div>
                        </div>

                        {infoError && (
                            <p className="partners-upload-page__company-error">
                                {infoError}
                            </p>
                        )}
                    </div>
                </div>

                <div className="partners-upload-page__alert">
                    <span className="partners-upload-page__alert-label">Важно</span>
                    <p className="partners-upload-page__alert-text">
                        На этой странице нужно загрузить фото- или видеоотчёт о показе рекламы
                        {adTitle ? ` «${adTitle}»` : ''} по указанному адресу.
                    </p>
                    <p className="partners-upload-page__alert-text">
                        Пожалуйста, предоставьте отчёт в течение трёх дней, чтобы мы могли
                        подтвердить размещение рекламы.
                    </p>
                </div>
            </header>

            {(adTitle || adUrl) && (
                <section className="partners-upload-page__command">
                    <button
                        type="button"
                        className="partners-upload-page__command-button"
                        onClick={handlePlayCommand}
                        disabled={commandSending || !partnerCode || !adId}
                    >
                        {commandSending
                            ? 'Отправляем команду...'
                            : 'Начать воспроизводить ролик '}
                    </button>
                    <p className="partners-upload-page__command-note">
                        Работает только на последней версии плеера с 1.2.0
                    </p>
                    {commandMessage && (
                        <p className="partners-upload-page__command-message">
                            {commandMessage}
                        </p>
                    )}
                </section>
            )}

            {(adTitle || adUrl) && (
                <section className="partners-upload-page__ad-section">
                    <h2 className="partners-upload-page__ad-title">Ролик для проверки</h2>
                    {adTitle && (
                        <p className="partners-upload-page__ad-text">
                            Название ролика: <strong>{adTitle}</strong>
                        </p>
                    )}
                    {renderAdMedia()}
                    <p className="partners-upload-page__ad-hint">
                        Убедитесь, что именно этот ролик отображается на экране по указанному адресу
                        и загрузите актуальные фото или видео с места размещения.
                    </p>
                </section>
            )}

            <section className="partners-upload-page__requirements">
                <h2 className="partners-upload-page__requirements-title">
                    Требования к отчёту
                </h2>

                <div className="partners-upload-page__requirements-grid">
                    <div className="partners-upload-page__requirements-block">
                        <h3 className="partners-upload-page__requirements-subtitle">
                            1. Фото
                        </h3>
                        <ul className="partners-upload-page__requirements-list">
                            <li>
                                Сделайте одно общее фото, охватывающее максимум экранов в салоне.
                            </li>
                            <li>
                                Загрузите от 1 до 3 фотографий.
                            </li>
                            <li>
                                Снимки должны быть чёткими.
                            </li>
                            <li>
                                Ролик бренда должен быть узнаваем хотя бы на одном из экранов.
                            </li>
                        </ul>
                        <div className="partners-upload-page__requirements-photo">
                            <img
                                src="https://s3.ru1.storage.beget.cloud/6afb2ec4a0bc-displaypromo/photo_2025-11-06_10-21-22.jpg"
                                alt="Пример фото отчёта"
                                className="partners-upload-page__requirements-photo-image"
                            />
                        </div>
                    </div>

                    <div className="partners-upload-page__requirements-block">
                        <h3 className="partners-upload-page__requirements-subtitle">
                            2. Видео
                        </h3>
                        <ul className="partners-upload-page__requirements-list">
                            <li>
                                Снимите короткое видео длительностью 15–30 секунд средним планом.
                            </li>
                            <li>
                                Покажите все или большинство экранов с транслируемым роликом.
                            </li>
                        </ul>
                        <div className="partners-upload-page__requirements-video">
                            <video
                                controls
                                poster="https://s3.ru1.storage.beget.cloud/6afb2ec4a0bc-displaypromo/photo_2025-11-06_10-21-22.jpg"
                                className="partners-upload-page__requirements-video-preview"
                            >
                                <source
                                    src="https://s3.ru1.storage.beget.cloud/6afb2ec4a0bc-displaypromo/WhatsApp Video 2025-09-16 at 16.52.58.mp4"
                                    type="video/mp4"
                                />
                                Ваш браузер не поддерживает элемент video.
                            </video>
                        </div>
                    </div>
                </div>
            </section>

            <section className="partners-upload-page__content">
                <div
                    className={
                        'partners-upload-page__dropzone' +
                        (isDragging ? ' partners-upload-page__dropzone--dragging' : '')
                    }
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                >
                    <div className="partners-upload-page__dropzone-inner">
                        <div className="partners-upload-page__dropzone-icon">⬆</div>
                        <h2 className="partners-upload-page__dropzone-title">
                            Загрузите фото и видео отчёта
                        </h2>
                        <p className="partners-upload-page__dropzone-subtitle">
                            Перетащите файлы в эту область или выберите их на устройстве.
                            Нам важно получить подтверждение фактического показа рекламы
                            {adTitle ? ` «${adTitle}»` : ''}.
                        </p>
                        <label className="partners-upload-page__button">
                            {uploading ? 'Загрузка...' : 'Выбрать файлы'}
                            <input
                                type="file"
                                multiple
                                accept="image/*,video/*"
                                onChange={onInputChange}
                                disabled={uploading}
                            />
                        </label>
                        <p className="partners-upload-page__dropzone-hint">
                            Поддерживаются изображения и видео стандартных форматов.
                        </p>
                        {uploadError && (
                            <p
                                className="partners-upload-page__dropzone-hint"
                                style={{ color: '#fca5a5', marginTop: '6px' }}
                            >
                                {uploadError}
                            </p>
                        )}
                    </div>
                </div>

                {items.length > 0 && (
                    <div className="partners-upload-page__gallery">
                        <div className="partners-upload-page__gallery-header">
                            <h3 className="partners-upload-page__gallery-title">
                                Загруженные файлы
                            </h3>
                            <span className="partners-upload-page__gallery-count">
                                {items.length}
                            </span>
                        </div>

                        <div className="partners-upload-page__grid">
                            {items.map((item) => {
                                const name =
                                    item.name ||
                                    (item.type === 'video' ? 'Видео отчёт' : 'Фото отчёт');
                                const sizeBytes =
                                    item.size ?? (item.file ? item.file.size : null);
                                const sizeMb =
                                    sizeBytes != null
                                        ? (sizeBytes / 1024 / 1024).toFixed(1)
                                        : null;

                                return (
                                    <button
                                        type="button"
                                        key={item.id}
                                        className="partners-upload-page__card"
                                        onClick={() => openPreview(item)}
                                    >
                                        <div className="partners-upload-page__card-media">
                                            {item.type === 'image' ? (
                                                <img
                                                    src={item.url}
                                                    alt={name}
                                                    className="partners-upload-page__card-image"
                                                />
                                            ) : (
                                                <video
                                                    src={item.url}
                                                    className="partners-upload-page__card-video"
                                                    muted
                                                />
                                            )}
                                            {item.type === 'video' && (
                                                <span className="partners-upload-page__card-badge">
                                                    Видео
                                                </span>
                                            )}
                                            {item.uploaded && (
                                                <span
                                                    className="partners-upload-page__card-badge"
                                                    style={{ right: 8, left: 'auto' }}
                                                >
                                                    ✔
                                                </span>
                                            )}
                                        </div>
                                        <div className="partners-upload-page__card-info">
                                            <p
                                                className="partners-upload-page__card-name"
                                                title={name}
                                            >
                                                {name}
                                            </p>
                                            <p className="partners-upload-page__card-meta">
                                                {sizeMb != null ? `${sizeMb} МБ` : ''}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>

            {previewItem && (
                <div className="partners-upload-page__preview" onClick={closePreview}>
                    <div
                        className="partners-upload-page__preview-dialog"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="partners-upload-page__preview-close"
                            type="button"
                            onClick={closePreview}
                        >
                            ✕
                        </button>
                        <div className="partners-upload-page__preview-media">
                            {previewItem.type === 'image' ? (
                                <img
                                    src={previewItem.url}
                                    alt={previewItem.name || 'Файл'}
                                />
                            ) : (
                                <video
                                    controls
                                    autoPlay
                                    src={previewItem.url}
                                />
                            )}
                        </div>
                        <div className="partners-upload-page__preview-info">
                            <p className="partners-upload-page__preview-name">
                                {previewItem.name || 'Файл'}
                            </p>
                            <p className="partners-upload-page__preview-meta">
                                {previewItem.size
                                    ? (previewItem.size / 1024 / 1024).toFixed(2) + ' МБ'
                                    : ''}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadPage;
